import { decodeEventLog, type Address, type Hex, type TransactionReceipt } from 'viem';

import type { ApiTransaction } from 'types';
import { transferId, evmParty } from 'exposure/transferId';

import { multiSigLockerAbi } from './bridgeService';

export function sourceEventKey(
  source: Pick<ApiTransaction, 'direction' | 'evmChainId' | 'sourceTxHash' | 'sourceEventIndex'>
): string | null {
  const { evmChainId, sourceTxHash, sourceEventIndex } = source;
  if (
    source.direction !== 'eth_to_casper' ||
    !sourceTxHash ||
    !Number.isSafeInteger(evmChainId) ||
    evmChainId! <= 0 ||
    !Number.isSafeInteger(sourceEventIndex) ||
    sourceEventIndex! < 0
  )
    return null;

  return `evm:${evmChainId}:${sourceTxHash.toLowerCase()}:${sourceEventIndex}`;
}

/** The commitment this page submitted, as recorded by its receipt. */
export interface ReceiptCommitment {
  /** Block-wide log index of the `TransferCommitted` event. */
  eventIndex: number;
  /** The committed transfer (`0x` + 64 lowercase hex). */
  transferId: Hex;
  /** The gateway message carrying it. */
  sendId: Hex;
  sourceSequence: bigint;
}

/**
 * The UI submits one route commitment (`lock` or `burnWrapped`) per transaction. Resolve only
 * one `TransferCommitted` of the locker whose route and amount are the submitted ones and whose
 * `transferId` recomputes from the event's fields plus the sender, recipient and decimals this
 * page signed for (specification section 3.3): that proves it is this transfer, not a sibling
 * commitment of the same transaction. Anything else stays unresolved.
 */
export function receiptCommitment(
  receipt: TransactionReceipt,
  expected: {
    hash: string;
    locker: Address;
    routeId: Hex;
    sender: string;
    amount: bigint;
    decimals: number;
    recipientKind: number;
    recipientValue: Hex;
  }
): ReceiptCommitment | undefined {
  if (
    receipt.status !== 'success' ||
    receipt.transactionHash?.toLowerCase() !== expected.hash.toLowerCase()
  )
    return;
  const matches: ReceiptCommitment[] = [];
  for (const log of receipt.logs ?? []) {
    if (
      log.removed ||
      log.address.toLowerCase() !== expected.locker.toLowerCase() ||
      log.transactionHash?.toLowerCase() !== expected.hash.toLowerCase() ||
      !Number.isSafeInteger(log.logIndex) ||
      log.logIndex! < 0
    )
      continue;
    try {
      const event = decodeEventLog({
        abi: multiSigLockerAbi,
        data: log.data,
        topics: log.topics,
        strict: true,
      });
      if (event.eventName !== 'TransferCommitted') continue;
      const a = event.args;
      if (a.routeId.toLowerCase() !== expected.routeId.toLowerCase() || a.amount !== expected.amount) continue;
      const sender = evmParty(expected.sender);
      const id = transferId({
        routeId: a.routeId,
        sourceSequence: a.sourceSequence,
        senderKind: sender.kind,
        senderValue: sender.value,
        amount: a.amount,
        decimals: expected.decimals,
        sourcePolicyVersion: a.policyVersion,
        recipientKind: expected.recipientKind,
        recipientValue: expected.recipientValue,
        sourceCommitmentTimeMs: a.sourceCommitmentTimeMs,
      });
      if (id !== a.transferId.toLowerCase()) continue;
      matches.push({
        eventIndex: log.logIndex!,
        transferId: id,
        sendId: a.sendId.toLowerCase() as Hex,
        sourceSequence: a.sourceSequence,
      });
    } catch {
      // Not a locker event this page knows, or not canonically encoded.
    }
  }

  return matches.length === 1 ? matches[0] : undefined;
}
